// 세리사무소 포털 우측 사이드바 — 네이버 웨일 방식의 56px 레일 + 396px 패널 (기획서 7).
//
// 메일·결재·회사 전환은 하루에 수십 번 스치는 행동이라 본문을 떠나면 안 된다. 그래서
// 레일은 상주하고, 아이콘을 누르면 패널이 밀려 나오되 본문은 그대로 있다.
// 고정(📌)하면 본문이 옆으로 밀리고 패널이 남아, 회사를 갈아타며 연속 작업이 된다.
//
// 이 사이드바의 성격은 일반 그룹웨어와 다르다: 앱의 목록이 아니라 '지금 보고 있는 고객사의
// 맥락'을 보여준다. 회사를 고르면 메일·결재·문서가 그 회사 것부터 정렬되고 표시가 붙는다.
//
// 내용은 목업이다(Sidebar.zeirishi.data.ts). 메일·결재·메신저·일정을 줄 모듈 API 가 아직
// 없고, 회사별 ERP 진입은 대리 권한 토큰이 선행되어야 한다 — 메뉴를 누르면 회계 모듈로만 간다.
import React, { useMemo, useState } from 'react';
import { navigate, usePathname } from '../../lib/navigation';
import { useZeirishi } from '../../pages/Home/Home.zeirishi.context';
import ZeirishiAiChat from '../../pages/Home/Home.zeirishi.chat';
import { AUTO_ITEMS, AUTO_TOTAL, COMPANIES, companyOf } from '../../pages/Home/Home.zeirishi.data';
import type { Company } from '../../pages/Home/Home.zeirishi.data';
import {
  CLIENT_TOTAL, ERP_CATS, HIST, HIST_ETC, OFFICE_CATS, RAIL_APPS, RAIL_DOCS, WDATA, WFOOT,
} from './Sidebar.zeirishi.data';
import type { HistRow, MenuCat, RailApp, WRow } from './Sidebar.zeirishi.data';
import styles from './Layout.zeirishi.module.css';

/* ── 아이콘 ── */
const ico = (d: string, filled?: boolean) => (
  <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke={filled ? 'none' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS: { [id: string]: JSX.Element } = {
  ai: ico('M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17l-1.9-5.1L4.5 10l5.6-1.4z', true),
  of: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7.5" height="7.5" rx="2" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
    </svg>
  ),
  home: ico('M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'),
  co: ico('M2.5 21h19M4.5 21V6.2a1 1 0 0 1 1-1h6.5a1 1 0 0 1 1 1V21M13 10.5h5.5a1 1 0 0 1 1 1V21M7.3 9h2.4M7.3 13h2.4M7.3 17h2.4'),
  log: ico('M3.5 12a8.5 8.5 0 1 0 2.6-6.1M3.5 4v5h5M12 8v4.5l3 1.8'),
  mail: ico('M3 5h18v14H3zM3 7l9 6 9-6'),
  appr: ico('M14 3v5h5M19 12V8l-5-5H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h5M15 18l2 2 4-4'),
  msg: ico('M21 12a8 8 0 0 1-11.5 7.2L4 21l1.8-5.5A8 8 0 1 1 21 12z'),
  cal: ico('M3 4h18v18H3zM3 10h18M8 2v4M16 2v4'),
  board: ico('M4 4h16v16H4zM8 9h8M8 13h8M8 17h5'),
  office: ico('M14 3v5h5M19 21V8l-5-5H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2zM9 12h6M9 16h6'),
  chamber: ico('M3 4h18v4H3zM5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4'),
};

/** 메뉴 수는 목록에서 센다 — 메뉴를 늘리면 머리말 숫자도 따라온다 */
const OFFICE_MENU_COUNT = OFFICE_CATS.reduce((a, g) => a + g.apps.length, 0);

/** 레일에 고정으로 붙는 넷 — AI · 우리 사무소 업무 · ERP · 업무 이력 */
const FIXED: { id: string; nm: string; sb: string; cl: string }[] = [
  { id: 'ai', nm: 'AI 어시스턴트', sb: '화면 맥락을 읽고 답합니다', cl: 'ai' },
  { id: 'of', nm: '우리 사무소 업무', sb: `세무법인 정담 · ${OFFICE_MENU_COUNT}개 메뉴`, cl: 'tile' },
  { id: 'co', nm: 'ERP', sb: `우리 회사 + 수임기업 ${CLIENT_TOTAL}`, cl: 'erp' },
  { id: 'log', nm: '업무 이력', sb: '최근 30일', cl: 'tax' },
];

const APP_BY_ID: { [id: string]: RailApp } = {};
RAIL_APPS.concat(RAIL_DOCS).forEach(a => { APP_BY_ID[a.id] = a; });

export default function SidebarZeirishi() {
  const {
    toast, currentCo, setCurrentCo, queue,
    panel: open, togglePanel: onToggle, pinned, togglePin: onPin, aiOutbound,
  } = useZeirishi();

  /* 수임기업 목록의 검색·필터 — 패널을 닫아도 유지된다 */
  const [coQuery, setCoQuery] = useState('');
  const [coFilter, setCoFilter] = useState<'todo' | 'due' | 'new' | 'all'>('todo');
  /* 회사 목록 ↔ 그 회사 콘솔 */
  const [coView, setCoView] = useState<'list' | 'detail'>('list');
  /* 업무 이력 필터 */
  const [logPeriod, setLogPeriod] = useState<'today' | 'yest' | 'week' | 'month'>('today');
  const [logKind, setLogKind] = useState<'all' | 'me' | 'au'>('all');

  const pathname = usePathname();
  const co = currentCo ? companyOf(currentCo) : null;
  const todoCount = COMPANIES.filter(c => c.todo > 0).length;

  /* ── 레일 ──
     기본은 패널 토글이지만, 홈처럼 이동하는 버튼도 있어 동작과 활성 판정을 받을 수 있게 뒀다. */
  const railButton = (
    id: string,
    label: string,
    cl: string,
    badge?: number,
    opts?: { active?: boolean; onClick?: () => void },
  ) => (
    <button
      key={id}
      type="button"
      className={`${styles.wb} ${(opts && opts.active !== undefined ? opts.active : open === id) ? styles.wbOn : ''}`}
      data-lb={label}
      title={label}
      onClick={opts && opts.onClick ? opts.onClick : () => onToggle(id)}
    >
      <span className={`${styles.tl} ${styles['tl_' + cl]}`}>{ICONS[id]}</span>
      {badge ? <span className={styles.bd}>{badge}</span> : null}
    </button>
  );

  /* ── 패널: 우리 사무소 업무 / 회사 ERP 메뉴 ── */
  const menuGrid = (cats: MenuCat[], onPick: (label: string) => void) => (
    <>
      {cats.map(cat => (
        <div key={cat.t}>
          <div className={styles.cosec}>
            <span className={`${styles.bar2} ${styles['bar_' + cat.cl]}`} />
            {cat.t}
          </div>
          <div className={styles.comenu}>
            {cat.apps.map(app => (
              <div key={app} className={styles.mrow} onClick={() => onPick(app)}>
                <span className={`${styles.mi} ${styles['tl_' + cat.cl]}`}>{ICONS.office}</span>
                <span className={styles.mn}>{app}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  /* 회사 컨텍스트로 들어가는 진입 — 권한 토큰이 없어 회계 모듈로만 보낸다 */
  const enterErp = (c: Company, menu: string) => {
    setCurrentCo(c.id);
    toast(`${c.nm} · ${menu} — 회계 모듈로 이동합니다. 회사 데이터 연결은 대리 진입 설계 후 붙습니다.`);
    navigate('/finance');
  };

  /* ── 패널: 수임기업 목록 ── */
  const filtered = useMemo(() => {
    const q = coQuery.trim();
    return COMPANIES.filter(c => {
      if (q && c.nm.indexOf(q) < 0 && c.ab.indexOf(q) < 0 && c.biz.indexOf(q) < 0) {
        return false;
      }
      if (coFilter === 'todo') return c.todo > 0;
      if (coFilter === 'due') return c.dueLv === 'd' || c.dueLv === 'w';
      if (coFilter === 'new') return !!c.nw;
      return true;
    });
  }, [coQuery, coFilter]);

  /* 칩에 붙는 수 — 검색어를 빼고 그 갈래에 몇 곳이 있는지만 센다.
     '전체'는 목록에 실린 9곳이 아니라 실제 수임기업 수다(목록은 그중 일부다). */
  const filterCount = (k: 'todo' | 'due' | 'new' | 'all') => {
    if (k === 'all') {
      return CLIENT_TOTAL;
    }
    return COMPANIES.filter(c => (
      k === 'todo' ? c.todo > 0 : k === 'due' ? (c.dueLv === 'd' || c.dueLv === 'w') : !!c.nw
    )).length;
  };

  const coList = () => {
    const own = filtered.filter(c => c.own);
    const rest = filtered.filter(c => !c.own);
    const row = (c: Company) => (
      <div
        key={c.id}
        className={`${styles.corow} ${c.own ? styles.corowOwn : ''} ${currentCo === c.id ? styles.corowOn : ''}`}
        onClick={() => { setCurrentCo(c.id); setCoView('detail'); }}
      >
        <span className={styles.ci5}>{c.ab}</span>
        <span className={styles.cb3}>
          <span className={styles.n4}>{c.nm}{c.own ? <span className={styles.ownbadge}>본인 회사</span> : null}</span>
          <span className={styles.m4}>기장 {c.prog}% · {c.dueLv ? <b>{c.due}</b> : c.due}</span>
        </span>
        {c.todo ? <span className={styles.bd3}>{c.todo}</span> : null}
        {/* 호버로만 보인다 — 줄마다 늘 붙어 있으면 배지와 다투고 목록이 시끄러워진다 */}
        <span className={styles.ent2}>열기 →</span>
      </div>
    );
    return (
      <>
        <div className={styles.copane}>
          <div className={styles.cosearch}>
            {ICONS.co}
            <input
              value={coQuery}
              placeholder="회사명 · 업종 검색"
              onChange={e => setCoQuery(e.target.value)}
            />
          </div>
          <div className={styles.cofilt}>
            {([['todo', '미처리 업무'], ['due', '마감 임박'], ['new', '신규 수임'], ['all', '전체']] as const).map(([k, lb]) => (
              <button
                key={k}
                type="button"
                className={coFilter === k ? styles.cofiltOn : undefined}
                onClick={() => setCoFilter(k)}
              >
                {lb}<span className={styles.n2}>{filterCount(k)}</span>
              </button>
            ))}
          </div>
        </div>
        {own.length > 0 && <div className={styles.cogrp}>우리 회사</div>}
        {own.map(row)}
        {rest.length > 0 && (
          <div className={styles.cogrp}>수임기업 {CLIENT_TOTAL}개사<span>목록 {rest.length}</span></div>
        )}
        {rest.map(row)}
        {filtered.length === 0 && <div className={styles.conone}>조건에 맞는 회사가 없습니다.</div>}
      </>
    );
  };

  /* ── 패널: 회사 콘솔 ── */
  const coDetail = (c: Company) => {
    /* 들어가서 확인할 것을 들어가기 전에 보여준다 (기획서 7.2) */
    const items = queue.liveItems.filter(i => i.co === c.id);
    return (
      <>
        <div className={styles.codet}>
          <div className={styles.codetHead}>
            <button type="button" className={styles.back} onClick={() => setCoView('list')} title="목록으로">←</button>
            <span className={styles.ci5}>{c.ab}</span>
            <span className={styles.n4}>{c.nm}{c.own ? <span className={styles.ownbadge}>본인 회사</span> : null}</span>
          </div>
          <div className={styles.costat}>
            <div className={styles.s3}><div className={styles.k3}>기장</div><div className={styles.v3}>{c.prog}%</div></div>
            <div className={styles.s3}><div className={styles.k3}>미처리 증빙</div><div className={styles.v3}>{c.evi}건</div></div>
            <div className={styles.s3}>
              <div className={styles.k3}>가장 급한 마감</div>
              <div className={`${styles.v3} ${c.dueLv === 'd' ? styles.v3d : c.dueLv === 'w' ? styles.v3w : ''}`}>{c.due}</div>
            </div>
          </div>
          <button type="button" className={styles.coent} onClick={() => enterErp(c, 'ERP 홈')}>
            ERP 홈으로 바로 진입 →
          </button>
        </div>

        {items.length > 0 && (
          <>
            <div className={styles.cosec}><span className={`${styles.bar2} ${styles.bar_erp}`} />확인이 필요한 업무 {items.length}건</div>
            <div className={styles.cotask}>
              {items.map(i => (
                <div key={i.id} className={styles.ctask} onClick={() => { queue.focus(i.id); onToggle('co'); }}>
                  <span className={styles.cd}>{i.due}</span>
                  <span className={styles.ct}>{i.title}</span>
                  <span className={styles.cg}>열기 →</span>
                </div>
              ))}
            </div>
          </>
        )}

        {menuGrid(ERP_CATS, app => enterErp(c, app))}
      </>
    );
  };

  /* ── 패널: 업무 이력 ── */
  const todayRows = (): HistRow[] => {
    const mine: HistRow[] = queue.doneItems.map(i => ({
      tm: queue.doneAt[i.id] || '',
      k: 'me',
      co: companyOf(i.co).nm,
      t: i.title,
    }));
    const auto: HistRow[] = AUTO_ITEMS.map(a => ({ tm: a.tm, k: 'au', co: a.who, t: a.title }));
    return mine.concat(auto).sort((a, b) => (a.tm < b.tm ? 1 : a.tm > b.tm ? -1 : 0));
  };

  const logPanel = () => {
    const today = { lb: '오늘 · 9월 16일 (수)', rows: todayRows() };
    const groups = logPeriod === 'today' ? [today]
      : logPeriod === 'yest' ? [HIST[0]]
        : [today].concat(HIST);
    const keep = (rows: HistRow[]) => (logKind === 'all' ? rows : rows.filter(r => r.k === logKind));
    const mineN = queue.doneIds.length;

    return (
      <>
        <div className={styles.lsum}>
          <div className={styles.s5}><div className={styles.k5}>내 승인</div><div className={`${styles.v5} ${styles.v5b}`}>{mineN}건</div></div>
          <div className={styles.s5}><div className={styles.k5}>AI 자동</div><div className={`${styles.v5} ${styles.v5p}`}>{AUTO_TOTAL}건</div></div>
          {/* 성공만 보여주는 이력은 감사 자료가 되지 못한다 (기획서 7.3) */}
          <div className={styles.s5}><div className={styles.k5}>보류·반송</div><div className={styles.v5}>0건</div></div>
        </div>
        <div className={styles.logfilt}>
          <div className={styles.logtabs}>
            {([['today', '오늘'], ['yest', '어제'], ['week', '이번 주'], ['month', '이번 달']] as const).map(([k, lb]) => (
              <button key={k} type="button" className={logPeriod === k ? styles.logtabOn : undefined} onClick={() => setLogPeriod(k)}>{lb}</button>
            ))}
          </div>
          <div className={`${styles.logtabs} ${styles.logtabsK}`}>
            {([['all', '전체'], ['me', '내 승인'], ['au', 'AI 자동']] as const).map(([k, lb]) => (
              <button key={k} type="button" className={logKind === k ? styles.logtabOnK : undefined} onClick={() => setLogKind(k)}>{lb}</button>
            ))}
          </div>
        </div>
        {groups.map(g => {
          const rows = keep(g.rows);
          return (
            <div key={g.lb}>
              <div className={styles.lgrp}>
                {g.lb}
                <span className={styles.lc}>
                  승인 {g.rows.filter(r => r.k === 'me').length} · 자동 {g.rows.filter(r => r.k === 'au').length}
                </span>
              </div>
              {rows.length === 0
                ? <div className={styles.conone}>해당하는 기록이 없습니다.</div>
                : rows.map((r, idx) => (
                  <div key={g.lb + idx} className={styles.lrow} onClick={() => toast(`${r.co} — ${r.t}`)}>
                    <span className={styles.lt}>{r.tm}</span>
                    {/* 책임 주체가 사람인지 시스템인지가 가장 중요한 구분이다 */}
                    <span className={`${styles.lk} ${r.k === 'me' ? styles.lkMe : styles.lkAu}`}>{r.k === 'me' ? '✓' : '⚡'}</span>
                    <span className={styles.lb5}>
                      <span className={styles.lco}>{r.co}</span>
                      <span className={styles.ltt}>{r.t}</span>
                    </span>
                  </div>
                ))}
            </div>
          );
        })}
        <div className={styles.lmore} onClick={() => toast('지난 이력 화면은 아직 없습니다')}>
          그 외 {HIST_ETC.n}건 · {HIST_ETC.lb}
        </div>
      </>
    );
  };

  /* ── 패널: 협업·문서 목록 ── */
  const appRows = (id: string) => {
    const rows = WDATA[id] || [];
    /* 회사를 고르면 그 회사 것이 위로 올라온다 — 앱의 목록이 아니라 고객사의 맥락이다 */
    const sorted = currentCo
      ? rows.slice().sort((a, b) => Number(b.co === currentCo) - Number(a.co === currentCo))
      : rows;
    const hl = (r: WRow) => (currentCo && r.co === currentCo ? styles.wrowHl : '');

    if (id === 'mail') {
      return sorted.map((r, i) => (
        <div key={i} className={`${styles.wrow} ${hl(r)}`} onClick={() => toast(`메일을 엽니다 — ${r.sj}`)}>
          <span className={`${styles.av2} ${r.un ? styles.av2b : ''}`}>{(r.who || '').slice(0, 1)}</span>
          <span className={styles.wb2}>
            <span className={styles.l1}>
              <span className={styles.who}>{r.who}</span>
              <span className={styles.wtag}>{r.org}</span>
              <span className={styles.tm}>{r.tm}</span>
            </span>
            <span className={styles.sj}>{r.sj}</span>
            <span className={styles.pv}>{r.pv}</span>
          </span>
          {r.un ? <span className={styles.un} /> : null}
        </div>
      ));
    }

    if (id === 'appr') {
      return sorted.map((r, i) => (
        <div key={i} className={`${styles.wrow} ${hl(r)}`} onClick={() => toast(`결재 문서를 엽니다 — ${r.tt}`)}>
          <span className={styles.wb2}>
            <span className={styles.l1}>
              {r.tag ? <span className={`${styles.wtag} ${styles['wtag_' + (r.tag[1] || 'n')]}`}>{r.tag[0]}</span> : null}
              <span className={styles.tm}>{r.tm}</span>
            </span>
            <span className={styles.sj}>{r.tt}</span>
            <span className={styles.pv}>{r.md} · {r.who}</span>
          </span>
        </div>
      ));
    }

    if (id === 'msg') {
      return sorted.map((r, i) => (
        <div key={i} className={`${styles.wrow} ${hl(r)}`} onClick={() => toast(`대화창을 엽니다 — ${r.who}`)}>
          <span className={`${styles.av2} ${r.un ? styles.av2b : ''}`}>{r.grp ? '#' : (r.who || '').slice(0, 1)}</span>
          <span className={styles.wb2}>
            <span className={styles.l1}><span className={styles.who}>{r.who}</span><span className={styles.tm}>{r.tm}</span></span>
            <span className={styles.pv1}>{r.pv}</span>
          </span>
          {r.un ? <span className={styles.unN}>{r.un}</span> : null}
        </div>
      ));
    }

    if (id === 'cal') {
      return sorted.map((r, i) => (
        <div key={i} className={styles.wrow} onClick={() => toast(`일정을 엽니다 — ${r.tt}`)}>
          <span className={styles.wtime}>{r.tm}</span>
          <span className={styles.wb2}>
            <span className={styles.l1}>
              <span className={styles.who}>{r.tt}</span>
              {r.tag && r.tag[0] ? <span className={`${styles.wtag} ${styles['wtag_' + (r.tag[1] || 'n')]}`}>{r.tag[0]}</span> : null}
            </span>
            <span className={styles.pv1}>{r.md}</span>
          </span>
        </div>
      ));
    }

    if (id === 'board') {
      return sorted.map((r, i) => (
        <div key={i} className={styles.wrow} onClick={() => toast(`게시글을 엽니다 — ${r.tt}`)}>
          <span className={styles.wb2}>
            <span className={styles.l1}><span className={styles.who}>{r.tt}</span><span className={styles.tm}>{r.tm}</span></span>
            <span className={styles.pv}>{r.md}</span>
            <span className={styles.tagLine}><span className={styles.wtag}>{r.who}</span></span>
          </span>
          {r.un ? <span className={styles.un} /> : null}
        </div>
      ));
    }

    // ONEFFICE · ONECHAMBER
    const what = id === 'office' ? 'ONEFFICE에서 문서를 엽니다' : 'ONECHAMBER 보관함을 엽니다';
    return sorted.map((r, i) => (
      <div key={i} className={`${styles.wrow} ${hl(r)}`} onClick={() => toast(`${what} — ${r.tt}`)}>
        <span className={styles.wb2}>
          <span className={styles.l1}><span className={styles.who}>{r.tt}</span><span className={styles.tm}>{r.tm}</span></span>
          <span className={styles.pv1}>{r.md}</span>
          {r.tag && r.tag[0] ? <span className={styles.tagLine}><span className={`${styles.wtag} ${styles['wtag_' + (r.tag[1] || 'n')]}`}>{r.tag[0]}</span></span> : null}
        </span>
      </div>
    ));
  };

  /* ── 패널 머리·본문 고르기 ── */
  const fixed = FIXED.filter(f => f.id === open)[0];
  const app = open ? APP_BY_ID[open] : undefined;
  const head = fixed
    ? { nm: fixed.nm, sb: fixed.sb, cl: fixed.cl, n: 0 }
    : app
      ? { nm: app.nm, sb: app.sb, cl: app.cl, n: app.n }
      : null;

  const body = () => {
    if (open === 'ai') {
      return null;   // AI 는 아래에서 따로 — 언마운트되면 안 되기 때문이다
    }
    if (open === 'of') {
      return (
        <>
          {/* 이 패널과 ERP 패널을 헷갈리지 않게 — 하나는 우리 사무소, 하나는 고객사다 */}
          <div className={styles.ofhead}>
            수임기업 <b>{CLIENT_TOTAL}개사</b>를 관리하는 우리 사무소 자체 메뉴입니다.<br />
            각 회사 ERP는 <b>ERP</b> 패널에서 여세요.
          </div>
          {menuGrid(OFFICE_CATS, app2 => toast(`${app2} — 사무소 업무 화면은 아직 없습니다`))}
        </>
      );
    }
    if (open === 'co') {
      return coView === 'detail' && co ? coDetail(co) : coList();
    }
    if (open === 'log') {
      return logPanel();
    }
    return open ? appRows(open) : null;
  };

  /* 회사 콘솔(detail)에는 하단 버튼을 달지 않는다 — 한 회사 안에 들어와 있는데
     '신규 수임 등록'이 함께 놓이면 무엇에 대한 동작인지 흐려진다(시안도 목록에만 둔다). */
  const foot = open && !(open === 'co' && coView === 'detail') ? WFOOT[open] : undefined;

  return (
    <>
      {/* ── 레일 ── */}
      <div className={styles.wrail}>
        {railButton('ai', 'AI 어시스턴트', 'ai')}
        <div className={styles.wsep} />
        {/* 홈 — 모듈 화면(회계 등)에 들어갔을 때 포털 본문으로 돌아오는 길.
            패널을 여는 다른 버튼과 달리 경로를 바꾸므로, 열린 패널은 그대로 둔다. */}
        {railButton('home', '홈', 'home', 0, {
          active: pathname === '/',
          onClick: () => navigate('/'),
        })}
        {railButton('of', '우리 사무소 업무', 'tile')}
        {railButton('co', 'ERP', 'erp', todoCount)}
        {railButton('log', '업무 이력', 'tax')}
        <div className={styles.wsep} />
        {RAIL_APPS.map(a => railButton(a.id, a.nm, a.cl, a.n))}
        <div className={styles.wsep} />
        {RAIL_DOCS.map(a => railButton(a.id, a.nm, a.cl, a.n))}
        <div className={styles.grow} />
        <button
          type="button"
          className={`${styles.wpinb} ${pinned ? styles.wpinbOn : ''}`}
          title={pinned ? '고정 해제' : '사이드바 고정 — 본문이 옆으로 밀립니다'}
          onClick={onPin}
        >
          📌
        </button>
      </div>

      {/* ── 패널 ── */}
      <aside className={`${styles.wpanel} ${open ? styles.wpanelOn : ''}`}>
        {head && (
          <div className={styles.wph}>
            <span className={`${styles.tl} ${styles['tl_' + head.cl]}`}>{ICONS[open as string]}</span>
            <div>
              <div className={styles.wphNm}>{head.nm}</div>
              <div className={styles.wphSb}>{head.sb}{head.n ? ` · 새 ${head.n}건` : ''}</div>
            </div>
            <div className={styles.wphActs}>
              <button type="button" title="새 창으로 열기" onClick={() => toast(`${head.nm}을(를) 새 창으로 엽니다`)}>↗</button>
              <button type="button" title="닫기" onClick={() => onToggle(open as string)}>✕</button>
            </div>
          </div>
        )}

        {/* 지금 무엇을 기준으로 보고 있는지 먼저 밝힌다 */}
        {co && open !== 'co' && open !== 'log' && open !== 'of' && (
          <div className={styles.wctx}><span className={styles.wctxD} />{co.nm} 관련 항목을 위로 모았습니다</div>
        )}

        <div className={styles.wbody} style={open === 'ai' ? { padding: 0, display: 'flex' } : undefined}>
          {body()}
        </div>

        {/* AI 대화는 패널을 닫아도 살려 둔다 — 대화 ID·첨부 색인을 들고 있다 */}
        <div className={styles.aiSlot} style={open === 'ai' ? undefined : { display: 'none' }}>
          <ZeirishiAiChat
            outbound={aiOutbound}
            suggestions={[
              '이번 달 마감 전에 확인해야 할 항목을 정리해줘',
              '첨부한 결산 자료에서 전기 대비 크게 변동된 계정을 찾아줘',
              '소비세 신고에서 과세·비과세 구분이 헷갈리는 항목을 설명해줘',
            ]}
          />
        </div>

        {foot && open !== 'ai' && (
          <div className={styles.wfoot}>
            <button type="button" onClick={() => toast(`${foot[0]} — 아직 연결되지 않았습니다`)}>{foot[0]}</button>
            <button type="button" className={styles.wfootP} onClick={() => toast(`${foot[1]} — 아직 연결되지 않았습니다`)}>{foot[1]}</button>
          </div>
        )}
        {open === 'log' && (
          <div className={styles.wfoot}>
            <button type="button" onClick={() => toast('감사 로그를 내려받습니다')}>감사 로그 내려받기</button>
          </div>
        )}
      </aside>
    </>
  );
}
